/**
 * The app's three interface languages, their names in their own language, and
 * the rule for which one the interface is actually shown in. Pure: no i18next
 * import, so the Node probe can load it. Re-exported from `./index`.
 */
export const SUPPORTED_LANGS = ['en', 'es', 'pt'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/** Each language's own name (autonyms), the same in every interface language. */
export const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  es: 'Español',
  pt: 'Português',
};

function asSupported(code: string | null | undefined): SupportedLang | null {
  if (!code) return null;
  const base = code.toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGS as readonly string[]).includes(base) ? (base as SupportedLang) : null;
}

/**
 * The language the interface is shown in. i18next's `resolvedLanguage` comes
 * first (the language whose strings are actually used), then the base of the
 * requested code, then English, the fallback. So a regional browser language
 * (`es-MX`, `pt-BR`) shows as its base and an unsupported one (`fr-FR`) as
 * English. Used for the selected language control and for `<html lang>`.
 */
export function resolveDisplayLang(
  resolved: string | null | undefined,
  requested: string | null | undefined,
): SupportedLang {
  return asSupported(resolved) ?? asSupported(requested) ?? 'en';
}
